<script setup lang="ts">
import { ref, computed, onUnmounted } from 'vue';
import NewHeader from '~/components/NewHeader.vue';
import NewFooter from '~/components/NewFooter.vue';
import AiToolLanding from '~/components/AiToolLanding.vue';
import EditorCTA from '~/components/EditorCTA.vue';
import api from '~/api';
import axios from 'axios';

const authStore = useAuthStore();
const router = useRouter();
const localePath = useLocalePath();

useHead({
  title: 'AI Video Generator (Image-to-Video) - Best Face Swap',
  meta: [
    { name: 'description', content: 'Turn any image into a 5-10 second AI video. HD or FHD output, multiple aspect ratios. Powered by AiPose 4.0.' },
  ],
});

const ASSETS = 'https://cdn.treekee.com/ai-undress/images/nsfw-ai-video-generator';

// 拉真实工作台的 ai-video 效果做 gallery 展示（默认前 6 个，自动播放）
const { data: effectsData } = await useAsyncData('ai-video-gallery', () =>
  $fetch<{ code: number; data: { effects: Array<{ id: string; name: string; thumbnail: string; video?: string | null }> } }>(
    '/api/effects/list',
    { method: 'POST', body: { tool: 'ai-video' } },
  ),
);
const videoEffects = computed(() =>
  (effectsData.value?.data?.effects || []).filter((e) => !!e.video),
);
const galleryItems = computed(() =>
  videoEffects.value
    .slice(0, 3)
    .map((e) => ({ src: e.thumbnail, video: e.video!, title: e.name, alt: e.name })),
);
// 用真实效果填充 features 区（跳过 gallery 已用的前 3 个）
const featureItems = computed(() => {
  const labels = [
    { title: 'Realistic Motion', description: 'Natural movement coherent with the source image.' },
    { title: 'Multiple Aspect Ratios', description: 'Wide cinematic, vertical for short-form, or square for social.' },
    { title: 'HD & FHD Output', description: 'Crisp results suitable for any platform.' },
  ];
  return labels.map((l, i) => {
    const e = videoEffects.value[3 + i] || videoEffects.value[i];
    return {
      title: l.title,
      description: l.description,
      video: e ? { src: e.video!, poster: e.thumbnail } : undefined,
    };
  }).filter((x) => !!x.video);
});

const config = computed(() => ({
  hero: {
    title: 'AI Video Generator',
    description: 'Turn a still image into a short AI-generated video. Pick aspect ratio, resolution, and duration — get a clip in minutes.',
    videoSrc: `${ASSETS}/hero-video.mp4`,
    videoPoster: `${ASSETS}/hero-video.webp`,
  },
  stats: {
    items: [
      { value: '5–10s', label: 'Video Length' },
      { value: 'HD/FHD', label: 'Output Quality' },
      { value: '16:9 / 9:16 / 1:1', label: 'Aspect Ratios' },
      { value: '<3min', label: 'Generation Time' },
    ],
  },
  gallery: galleryItems.value.length ? {
    title: 'Examples Created with AI Video',
    description: 'See what kinds of motion our AI can generate from a single still image.',
    items: galleryItems.value,
  } : undefined,
  imageFeatures: featureItems.value.length ? {
    title: 'AiPose 4.0 — High-Fidelity Image-to-Video',
    description: 'Smooth motion, sharp detail, fast turnaround.',
    items: featureItems.value,
  } : undefined,
  usage: {
    title: 'How It Works',
    steps: [
      { title: 'Upload an Image', description: 'Pick a still photo as the starting frame.' },
      { title: 'Describe the Motion', description: 'Write a short prompt for the action or scene.' },
      { title: 'Pick Settings & Generate', description: 'Choose aspect, HD/FHD, 5s/10s — wait 1–3 minutes.' },
    ],
  },
  faq: {
    title: 'Frequently Asked Questions',
    items: [
      { question: 'How are credits priced?', answer: 'HD-5s = 24, HD-10s = 48, FHD-5s = 72, FHD-10s = 144 credits. Failed generations are refunded.' },
      { question: 'How long does a generation take?', answer: '5-second videos finish in 1–2 minutes; 10-second clips usually finish in 2–3 minutes.' },
      { question: 'What aspect ratios are supported?', answer: '16:9 (landscape), 9:16 (vertical), and 1:1 (square).' },
      { question: 'Are my uploads kept private?', answer: 'Uploads are used only to drive the generation and are removed automatically afterwards.' },
    ],
  },
  cta: {
    title: 'Ready to Animate Your Image?',
    description: 'Upload a photo and get your first AI video in minutes.',
    buttonText: 'Get Started',
    buttonHref: '#hero',
  },
}));

// ===== 交互逻辑 =====
const POLL_INTERVAL = 60000;
const MAX_POLLS = 5;

const sourceImage = ref('');
const prompt = ref('');
const aspect = ref<'16:9' | '9:16' | '1:1'>('16:9');
const resolution = ref<'HD' | 'FHD'>('HD');
const duration = ref<'5' | '10'>('5');

const submitting = ref(false);
const polling = ref(false);
const pollTimer = ref<any>(null);
const resultUrl = ref('');
const errorMsg = ref('');
const orderId = ref('');
const taskId = ref('');
const isUploading = ref(false);

const COSTS: Record<string, number> = { 'HD-5': 24, 'HD-10': 48, 'FHD-5': 72, 'FHD-10': 144 };
const cost = computed(() => COSTS[`${resolution.value}-${duration.value}`] || 24);
const canSubmit = computed(() =>
  !submitting.value && !polling.value && prompt.value.trim().length >= 3 && !!sourceImage.value,
);

async function uploadFile(event: Event) {
  const input = event.target as HTMLInputElement;
  const file = input.files?.[0];
  if (!file) return;
  isUploading.value = true;
  try {
    const ext = (file.name.split('.').pop() || 'png').toLowerCase();
    const sign = await api.getSignUrl(ext);
    await axios.put(sign.sign_url, file, {
      headers: { 'Content-Type': file.type || 'application/octet-stream' },
    });
    sourceImage.value = (sign as any).file_url || `https://cdn.treekee.com/bestfaceswap/uploads/${sign.material_id}.${ext}`;
  } catch (e) {
    console.error('upload failed', e);
    alert('Upload failed');
  } finally {
    isUploading.value = false;
    input.value = '';
  }
}

async function submit() {
  if (!authStore.isLogin) {
    router.push(localePath('/login/google?redirect=/ai-video'));
    return;
  }
  errorMsg.value = '';
  resultUrl.value = '';
  submitting.value = true;
  try {
    const r = await api.createAiVideo({
      image_url: sourceImage.value,
      prompt: prompt.value.trim(),
      aspect_ratio: aspect.value,
      resolution: resolution.value,
      duration: duration.value,
    });
    orderId.value = r.order_id;
    taskId.value = r.task_id;
    submitting.value = false;
    polling.value = true;
    startPolling();
  } catch (e: any) {
    submitting.value = false;
    errorMsg.value = e?.message || 'Submission failed';
  }
}

function startPolling() {
  let attempts = 0;
  pollTimer.value = setInterval(async () => {
    attempts++;
    try {
      const r = await api.fetchAiVideoRes(orderId.value, taskId.value);
      if (r.task_status === 1) {
        resultUrl.value = r.url;
        stopPolling();
      } else if (r.task_status === -1) {
        errorMsg.value = r.message || 'Generation failed';
        stopPolling();
      } else if (attempts >= MAX_POLLS) {
        errorMsg.value = 'Timeout — task may still be processing in background';
        stopPolling();
      }
    } catch {/* ignore */}
  }, POLL_INTERVAL);
}
function stopPolling() {
  polling.value = false;
  if (pollTimer.value) { clearInterval(pollTimer.value); pollTimer.value = null; }
}
function reset() { resultUrl.value = ''; errorMsg.value = ''; taskId.value = ''; orderId.value = ''; }
onUnmounted(() => stopPolling());
</script>

<template>
  <div class="bg-neutral-950 min-h-screen">
    <NewHeader />
    <AiToolLanding :config="config">
      <template #form>
        <EditorCTA tool="ai-video" />
        <div v-if="false" class="form-card">
          <div class="field">
            <label>Source Image <span class="req">*</span></label>
            <div v-if="sourceImage" class="single-thumb">
              <img :src="sourceImage" alt="" />
              <button class="thumb-x" @click="sourceImage = ''">×</button>
            </div>
            <label v-else class="big-upload">
              <input type="file" accept="image/*" class="hidden" :disabled="isUploading" @change="uploadFile" />
              <span v-if="isUploading">Uploading…</span>
              <span v-else>Click to upload an image</span>
            </label>
          </div>

          <div class="field">
            <label>Prompt <span class="req">*</span></label>
            <textarea v-model="prompt" rows="3" placeholder="Describe the motion/scene…" />
          </div>

          <div class="field">
            <label>Aspect Ratio</label>
            <div class="seg">
              <button :class="{ active: aspect === '16:9' }" @click="aspect = '16:9'">16:9</button>
              <button :class="{ active: aspect === '1:1' }" @click="aspect = '1:1'">1:1</button>
              <button :class="{ active: aspect === '9:16' }" @click="aspect = '9:16'">9:16</button>
            </div>
          </div>

          <div class="field">
            <label>Resolution</label>
            <div class="seg">
              <button :class="{ active: resolution === 'HD' }" @click="resolution = 'HD'">HD</button>
              <button :class="{ active: resolution === 'FHD' }" @click="resolution = 'FHD'">FHD</button>
            </div>
          </div>

          <div class="field">
            <label>Duration</label>
            <div class="seg">
              <button :class="{ active: duration === '5' }" @click="duration = '5'">5s</button>
              <button :class="{ active: duration === '10' }" @click="duration = '10'">10s</button>
            </div>
          </div>

          <div v-if="resultUrl" class="result">
            <video :src="resultUrl" controls autoplay loop muted />
            <div class="actions">
              <a :href="resultUrl" target="_blank" rel="noopener" class="btn-secondary">Open</a>
              <button class="btn-secondary" @click="reset">New</button>
            </div>
          </div>
          <div v-else-if="polling" class="result-placeholder">
            <div class="spinner" /><p>Generating video… 1–3 minutes</p>
          </div>

          <button class="cta-btn" :disabled="!canSubmit" @click="submit">
            <span v-if="submitting">Submitting…</span>
            <span v-else-if="polling">Generating…</span>
            <span v-else>Generate · {{ cost }} credits</span>
          </button>
          <p v-if="errorMsg" class="err">{{ errorMsg }}</p>
        </div>
      </template>
    </AiToolLanding>
    <NewFooter />
  </div>
</template>

<style scoped lang="scss">

.form-card {
  @apply rounded-2xl border border-neutral-800 bg-neutral-900/60 p-6 space-y-4 text-white;
}
.field { @apply space-y-2; }
.field label { @apply text-sm font-medium block; }
.req { @apply text-amber-400; }
.field textarea, .field input {
  @apply w-full rounded-md border border-neutral-700 bg-neutral-950/60 p-2.5 text-sm text-white placeholder-white/40
    focus:outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-500;
}
.single-thumb { @apply relative w-full aspect-square max-h-[300px] overflow-hidden rounded-lg border border-neutral-800; }
.single-thumb img { @apply size-full object-cover; }
.thumb-x { @apply absolute right-2 top-2 size-7 rounded-full bg-black/70 text-white text-base leading-none; }
.big-upload {
  @apply flex h-40 w-full cursor-pointer items-center justify-center rounded-lg border-2 border-dashed border-neutral-700
    text-white/60 hover:bg-neutral-900;
}
.seg { @apply flex gap-2; }
.seg button {
  @apply flex-1 rounded-full border border-neutral-700 bg-neutral-950/60 px-4 py-2 text-sm transition;
  &.active { @apply border-amber-500 bg-amber-500/10 text-amber-300; }
}
.cta-btn {
  @apply w-full rounded-full px-6 py-3 text-base font-semibold text-white transition-all disabled:opacity-40 disabled:cursor-not-allowed;
  background: linear-gradient(135deg, #f97316 0%, #ea580c 50%, #c2410c 100%);
}
.err { @apply text-sm text-red-400; }
.result { @apply space-y-2; }
.result video { @apply w-full rounded-xl object-contain max-h-[400px]; }
.actions { @apply flex gap-2 justify-center; }
.btn-secondary {
  @apply inline-flex items-center justify-center rounded-full border border-neutral-700 px-4 py-2 text-sm hover:bg-neutral-800 cursor-pointer text-white;
}
.result-placeholder { @apply text-center text-neutral-500 py-6 space-y-2; }
.spinner { @apply mx-auto size-8 rounded-full border-2 border-neutral-700 border-t-amber-500 animate-spin; }
</style>
